import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { Wrench, Plus, Tag, Clock, Pencil, Trash2 } from 'lucide-react';

const emptyForm = { category: '', name: '', description: '', estimated_hours: '', skill_requirements: '' };
const predefinedCategories = ['Electrical', 'Plumbing', 'HVAC', 'Carpentry', 'Painting', 'Cleaning', 'Other'];

export default function ServicesManagement() {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const response = await axiosInstance.get('/services/categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingService(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (service) => {
    setEditingService(service);
    setFormData({
      category: service.category || '',
      name: service.name,
      description: service.description || '',
      estimated_hours: String(service.estimated_hours || ''),
      skill_requirements: (service.skill_requirements || []).join(', ')
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const skills = formData.skill_requirements.split(',').map(s => s.trim()).filter(s => s);
    const payload = {
      ...formData,
      estimated_hours: parseFloat(formData.estimated_hours),
      skill_requirements: skills
    };
    try {
      if (editingService) {
        await axiosInstance.put(`/services/${editingService.id}`, payload);
        toast.success('Service updated successfully');
      } else {
        await axiosInstance.post('/services', payload);
        toast.success('Service created successfully');
      }
      setShowDialog(false);
      setFormData(emptyForm);
      setEditingService(null);
      fetchServices();
    } catch (error) {
      toast.error(editingService ? 'Failed to update service' : 'Failed to create service');
    }
  };

  const handleDelete = async (service) => {
    try {
      await axiosInstance.delete(`/services/${service.id}`);
      toast.success('Service deleted');
      setDeleteConfirm(null);
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  return (
    <div data-testid="services-management">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Services Management
        </h2>
        <p className="text-gray-600">Manage service categories and offerings</p>
      </div>

      <div className="mb-6">
        <Button onClick={openCreate} data-testid="add-service-btn">
          <Plus size={16} className="mr-2" />
          Add Service
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.keys(categories).length > 0 ? (
            Object.entries(categories).map(([category, services]) => (
              <div key={category}>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((service) => (
                    <Card
                      key={service.id}
                      className="backdrop-blur-sm bg-white/80 shadow-lg border-0 hover:shadow-xl transition-shadow"
                      data-testid={`service-card-${service.id}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{service.name}</CardTitle>
                            <CardDescription className="mt-1">{service.description}</CardDescription>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(service)} data-testid={`edit-svc-${service.id}`}>
                              <Pencil size={15} className="text-gray-500" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDeleteConfirm(service)} data-testid={`delete-svc-${service.id}`}>
                              <Trash2 size={15} className="text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock size={16} className="text-[#595959]" />
                          <span>{service.estimated_hours} hours</span>
                        </div>
                        {service.skill_requirements?.length > 0 && (
                          <div className="flex items-start gap-2 text-sm text-gray-600">
                            <Tag size={16} className="text-[#595959] mt-0.5" />
                            <div className="flex flex-wrap gap-1">
                              {service.skill_requirements.map((skill, idx) => (
                                <span key={idx} className="bg-gray-200 text-[#595959] px-2 py-0.5 rounded text-xs">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Wrench size={48} className="text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No services available</p>
                <p className="text-gray-400 text-sm">Add your first service to get started</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
            <DialogDescription>{editingService ? 'Update service details' : 'Create a new service offering'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="service-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {predefinedCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service Name</Label>
              <Input data-testid="service-name-field" placeholder="e.g., Fan Installation" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input data-testid="service-description-field" placeholder="Service description..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Estimated Hours</Label>
              <Input type="number" step="0.5" data-testid="service-hours-field" placeholder="2.0" value={formData.estimated_hours} onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Skill Requirements (comma-separated)</Label>
              <Input data-testid="service-skills-field" placeholder="Electrical, Basic Tools" value={formData.skill_requirements} onChange={(e) => setFormData({ ...formData, skill_requirements: e.target.value })} />
            </div>
            <Button type="submit" className="w-full" data-testid="submit-service-btn">
              {editingService ? 'Update Service' : 'Create Service'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Service</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-svc">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
